import { Button } from '@/global/components/Button'
import Menu from '@/global/components/Menu'
import { cn } from '@/lib/utils'
import React from 'react'
import { useNavigate } from 'react-router-dom'

type ButtonProps = Omit<React.ComponentProps<typeof Button>, 'href'>

interface IPropsMenuButton extends ButtonProps {
    children?: React.ReactNode
    href?: string
    startIcon: JSX.Element
    label?: string
    position?:
        | 'left'
        | 'right'
        | 'center'
        | 'top-left'
        | 'top-right'
        | 'top-center'
}

export const GridMenuButton = React.forwardRef<
    HTMLButtonElement,
    IPropsMenuButton
>(({ children, startIcon, label, ...rest }, ref) => {
    return (
        <Menu.Root id={rest.id} position={rest.position} arrow>
            <Menu.Trigger>
                <GridButton ref={ref} {...{ startIcon, label, ...rest }} />
            </Menu.Trigger>
            <Menu.List>{children}</Menu.List>
        </Menu.Root>
    )
})

GridMenuButton.displayName = 'GridMenuButton'

interface IGridButtonProps extends ButtonProps {
    href?: string
    onlyView?: number | number[]
    startIcon: JSX.Element
    label?: string
}

export const GridButton = React.forwardRef<HTMLButtonElement, IGridButtonProps>(
    ({ label, href, onClick, ...props }, ref) => {
        const navigate = useNavigate()

        const Icon = props.startIcon && !label ? props.startIcon : null
        const startIcon = props.startIcon && label ? props.startIcon : null

        return (
            <Button
                {...props}
                ref={ref}
                startIcon={startIcon}
                variant="text"
                className={cn(
                    'flex flex-row items-center',
                    'justify-start md:justify-center md:w-fit w-full',
                    'h-9 text-xs',
                    'hover:bg-primary/15',
                    props.className,
                )}
                onClick={e => {
                    if (href) {
                        e.preventDefault()
                        onClick?.(e)
                        navigate(href)
                    } else if (onClick) {
                        onClick(e)
                    }
                }}
            >
                {Icon}
                {!!label && <span>{label}</span>}
                {props.children}
            </Button>
        )
    },
)

GridButton.displayName = 'GridButton'

export function GridMenuItem({
    label,
    startIcon = <React.Fragment />,
    ...props
}: React.ComponentProps<typeof Menu.Item> & {
    startIcon?: JSX.Element
    label: string
}) {
    return (
        <Menu.Item {...props}>
            {React.cloneElement(startIcon, {
                className: cn('mr-1', startIcon.props.className),
            })}
            <span>{label}</span>
            {props.children}
        </Menu.Item>
    )
}
